'use client'

import { memo, useCallback, useMemo, useState, useEffect } from 'react'
import { Annotation, Annotation2 } from '@/types/annotation'
import { toast } from '@/components/ui/use-toast'
import { ExtendedTask, ExtendedTaskResult, TaskAction } from './task-types'
import { TaskCard } from './task-card'
import {
  createFormatValue,
  formatActionName,
  formatDateTime,
  formatStatus,
  getStatusIcon,
  styles
} from './task-utils'
import { Button } from '@/components/ui/button'
import { IconPlayerPlay } from '@tabler/icons-react'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'
import { Database } from '@/supabase/types'
import { Task } from '@/lib/types/task'
import { createClientComponentClient } from '@supabase/auth-helpers-nextjs'
import { useTranslation } from 'react-i18next'
import type { Components } from 'streamdown'
import { useSplitPaneNavigation } from '@/components/splitview/split-pane-context'

interface InstantTaskResult {
  task: Task
  results:
    | Array<{
        success: boolean
        function: string
        result?: any
        error?: string
      }>
    | {
        success: boolean
        function: string
        result?: any
        error?: string
      }
}

const AnnotationTask = memo(({ annotation }: { annotation: Annotation | Annotation2 }) => {
  const [isRunning, setIsRunning] = useState(false)
  const [runSuccess, setRunSuccess] = useState(false)
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({})
  const [uploadingPdfs, setUploadingPdfs] = useState<Record<string, boolean>>({})
  const { navigateToHref } = useSplitPaneNavigation()
  const supabase = createClientComponentClient<Database>()
  const { t } = useTranslation()

  const toggleExpand = useCallback((key: string) => {
    setExpandedKeys((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  // Memoize markdown components to pass to TaskCard
  const markdownComponents = useMemo<Components>(
    () => ({
      a: ({ node: _node, ...props }) => (
        <a
          {...props}
          className="inline-block max-w-[200px] truncate align-bottom text-primary hover:text-primary/80"
          target="_blank"
          rel="noopener noreferrer"
        />
      ),
      p: ({ children, ...props }: React.HTMLProps<HTMLParagraphElement>) => (
        <p className="mb-2 last:mb-0" {...props}>
          {children}
        </p>
      )
    }),
    []
  )

  const formatValue = useMemo(
    () => createFormatValue(expandedKeys, toggleExpand, markdownComponents, t),
    [expandedKeys, toggleExpand, markdownComponents, t]
  )

  const handlePdfUpload = useCallback(
    async (base64Pdf: string, taskId: string) => {
      try {
        // Remove data URL prefix if present
        const pdfData = base64Pdf.replace(/^data:application\/pdf;base64,/, '')

        // Convert base64 to Uint8Array for upload
        const binaryString = atob(pdfData)
        const bytes = new Uint8Array(binaryString.length)
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i)
        }

        // Generate filename with timestamp
        const fileName = `${taskId}-${Date.now()}.pdf`

        // Upload to Supabase storage
        const { data: fileData, error: uploadError } = await supabase.storage
          .from('files')
          .upload(fileName, bytes, {
            contentType: 'application/pdf',
            upsert: true
          })

        if (uploadError) {
          console.error('PDF upload error:', uploadError)
          throw uploadError
        }

        // Get public URL
        const {
          data: { publicUrl }
        } = supabase.storage.from('files').getPublicUrl(fileName)

        return publicUrl
      } catch (error) {
        console.error('Error uploading PDF:', error)
        throw error
      }
    },
    [supabase]
  )

  const processPdfResults = useCallback(
    async (results: any, taskId: string) => {
      if (Array.isArray(results)) {
        return Promise.all(
          results.map(async (result) => {
            if (result.function === 'generate_pdf' && result.result?.pdf) {
              setUploadingPdfs((prev) => ({ ...prev, [taskId]: true }))
              try {
                const publicUrl = await handlePdfUpload(result.result.pdf, taskId)
                return {
                  ...result,
                  result: {
                    ...result.result,
                    pdf: publicUrl
                  }
                }
              } catch (error) {
                console.error('Error processing PDF result:', error)
                toast({
                  title: 'PDF Upload Failed',
                  description: 'There was an error uploading the PDF. Please try again.',
                  variant: 'destructive'
                })
                return result
              } finally {
                setUploadingPdfs((prev) => ({ ...prev, [taskId]: false }))
              }
            }
            return result
          })
        )
      } else if (results.function === 'generate_pdf' && results.result?.pdf) {
        setUploadingPdfs((prev) => ({ ...prev, [taskId]: true }))
        try {
          const publicUrl = await handlePdfUpload(results.result.pdf, taskId)
          return {
            ...results,
            result: {
              ...results.result,
              pdf: publicUrl
            }
          }
        } catch (error) {
          console.error('Error processing PDF result:', error)
          toast({
            title: 'PDF Upload Failed',
            description: 'There was an error uploading the PDF. Please try again.',
            variant: 'destructive'
          })
          return results
        } finally {
          setUploadingPdfs((prev) => ({ ...prev, [taskId]: false }))
        }
      }
      return results
    },
    [handlePdfUpload]
  )

  const [tasks, setTasks] = useState<ExtendedTask[]>(() => {
    const taskResult =
      annotation.taskAutomation__createTask ||
      annotation.taskAutomation__editTask ||
      annotation.taskAutomation__listTasks ||
      annotation.taskAutomation__runTask ||
      annotation.taskAutomation__scheduleTask ||
      annotation.taskAutomation__createInstantTask

    if (!taskResult) return []

    const taskData = 'result' in taskResult ? taskResult.result : taskResult
    if (!taskData) return []

    // Handle error strings (e.g., "Authentication error: Auth session missing!")
    if (typeof taskData === 'string') {
      console.error('Task data is an error string:', taskData)
      return []
    }

    // Ensure taskData is an object before using 'in' operator
    if (typeof taskData !== 'object' || taskData === null) {
      console.error('Task data is not an object:', taskData)
      return []
    }

    // Handle instant task results
    if ('task' in taskData && 'results' in taskData) {
      const instantTaskData = taskData as InstantTaskResult
      const task = instantTaskData.task

      // Process the results and update state
      processPdfResults(instantTaskData.results, task.id)
        .then((processedResults) => {
          const updatedTask: ExtendedTask = {
            ...task,
            id: task.id || t('Something went wrong'),
            name: task.name || t('Try again'),
            description: task.description || '',
            status: task.status || 'completed',
            progress: 100,
            due_date: task.due_date || undefined,
            workspace_id: task.workspace_id,
            schedule: undefined,
            action: Array.isArray(processedResults)
              ? processedResults.map((result) => ({
                  function: result.function,
                  params:
                    result.function === 'generate_pdf'
                      ? {
                          content: result.result?.content?.content || result.result?.content || '',
                          format: result.result?.format || 'A4',
                          orientation: result.result?.orientation || 'portrait'
                        }
                      : {},
                  status: result.success ? 'completed' : 'failed',
                  result:
                    result.function === 'generate_pdf'
                      ? {
                          pdf: result.result?.pdf,
                          content: result.result?.content,
                          format: result.result?.format || 'A4',
                          orientation: result.result?.orientation || 'portrait'
                        }
                      : result.result,
                  error: result.error
                }))
              : {
                  function: processedResults.function,
                  params:
                    processedResults.function === 'generate_pdf'
                      ? {
                          content:
                            processedResults.result?.content?.content ||
                            processedResults.result?.content ||
                            '',
                          format: processedResults.result?.format || 'A4',
                          orientation: processedResults.result?.orientation || 'portrait'
                        }
                      : {},
                  status: processedResults.success ? 'completed' : 'failed',
                  result:
                    processedResults.function === 'generate_pdf'
                      ? {
                          pdf: processedResults.result?.pdf,
                          content: processedResults.result?.content,
                          format: processedResults.result?.format || 'A4',
                          orientation: processedResults.result?.orientation || 'portrait'
                        }
                      : processedResults.result,
                  error: processedResults.error
                }
          }
          setTasks([updatedTask])
        })
        .catch((error) => {
          console.error('Error processing PDF results:', error)
          toast({
            title: t('Error Processing PDF'),
            description: t('There was an error processing the PDF results. Please try again.'),
            variant: 'destructive'
          })
        })

      // Return initial state while processing
      const initialTask: ExtendedTask = {
        ...task,
        id: task.id || t('Something went wrong'),
        name: task.name || t('Try again'),
        description: task.description || '',
        status: 'pending' as const,
        progress: 0,
        due_date: task.due_date || undefined,
        workspace_id: task.workspace_id,
        schedule: undefined,
        action: Array.isArray(instantTaskData.results)
          ? instantTaskData.results.map((result) => ({
              function: result.function,
              params: {},
              status: 'pending' as const,
              result: undefined
            }))
          : {
              function: (instantTaskData.results as any).function,
              params: {},
              status: 'pending' as const,
              result: undefined
            }
      }
      return [initialTask]
    }

    const rawTasks = Array.isArray(taskData)
      ? taskData
      : taskData && typeof taskData === 'object' && 'tasks' in taskData
        ? (taskData as ExtendedTaskResult).tasks
        : [taskData]

    return rawTasks.map((task) => ({
      ...task,
      id: task.id || t('Something went wrong'),
      name: task.name || t('Try again'),
      description: task.description,
      status: task.status || 'error',
      progress: task.progress || 0,
      due_date: task.due_date,
      workspace_id: task.workspace_id,
      schedule: task.schedule,
      action: task.action
        ? Array.isArray(task.action)
          ? task.action.map((action: Partial<TaskAction>) => ({
              id: action?.id,
              function: action?.function || 'error',
              params: action?.params || {},
              status: action?.status,
              result: action?.result,
              error: action?.error
            }))
          : {
              function: task.action?.function || 'error',
              params: task.action?.params || {},
              result: task.action?.result,
              error: task.action?.error
            }
        : {
            function: 'custom',
            params: {}
          }
    }))
  })

  const handleRunNow = useCallback(
    async (taskId: string) => {
      setIsRunning(true)
      setRunSuccess(false)
      try {
        const response = await fetch('/api/tasks/run', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskId })
        })

        if (!response.ok) throw new Error(t('Failed to run task'))

        setRunSuccess(true)
        setTimeout(() => setRunSuccess(false), 3000)

        toast({
          title: t('Task started'),
          description: t('The task has been triggered to run now.')
        })
      } catch (error) {
        console.error('Error running task:', error)
        toast({
          title: t('Failed to run task'),
          description: t('There was an error trying to run the task.'),
          variant: 'destructive'
        })
      } finally {
        setIsRunning(false)
      }
    },
    [t]
  )

  const formatActionParams = useCallback(
    (params: Record<string, any>) => {
      return (
        <div className="space-y-2">
          {Object.entries(params).map(([key, value]) => (
            <div key={key} className="flex flex-col">
              <span className="text-xs capitalize text-muted-foreground">{key}:</span>
              {formatValue(key, value)}
            </div>
          ))}
        </div>
      )
    },
    [formatValue]
  )

  const handleRunTask = async (taskId: string) => {
    try {
      setIsRunning(true)

      // Find the task from our tasks array
      const task = tasks.find((t) => t.id === taskId)
      if (!task) throw new Error(t('Task not found'))

      const response = await authenticatedFetch('/api/chat/task', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          taskId: task.id,
          workspaceId: task.workspace_id
        })
      })

      if (!response.ok) {
        throw new Error(t('Failed to create task chat'))
      }

      const { chatId } = await response.json()
      navigateToHref(`/chat/${chatId}`)
    } catch (error) {
      console.error(error)
      toast({
        title: t('Error'),
        description: t('Failed to run task'),
        variant: 'destructive'
      })
    } finally {
      setIsRunning(false)
    }
  }

  return (
    <div className="space-y-4 p-4">
      <div className="divide-y rounded-md border p-4">
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            isRunning={isRunning}
            runSuccess={runSuccess}
            onRunNow={handleRunNow}
            expandedKeys={expandedKeys}
            onToggleExpand={toggleExpand}
            formatValue={formatValue}
            formatActionParams={formatActionParams}
            markdownComponents={markdownComponents}
          />
        ))}
      </div>
    </div>
  )
})

AnnotationTask.displayName = 'AnnotationTask'

export { AnnotationTask as Task }
